<template>
  <el-card v-loading="loading">
    <el-row :gutter="20">
      <el-col :span="10" :xs="24">
        <template v-if="enabled">
          <h3>Two-Factor Authentication</h3>

          <span
            >You've already enabled two-factor authentication using one time
            password authenticators.</span
          >
        </template>
        <template v-else>
          <h3>Register Two-Factor Authenticator</h3>

          <span
            >Increase your account's security by enabling Two-Factor
            Authentication (2FA)</span
          >
          <p>
            Use a one-time password authenticator on your mobile device or
            computer to enable 2FA.
          </p>
        </template>
        <div class="flex flex--align-center flex--center">
          <el-image :src="twoFaIcon" />
        </div>
      </el-col>

      <el-col :span="14" :xs="24">
        <template v-if="enabled">
          <h4>
            Status:
            <el-tag type="success" size="small"> Enabled </el-tag>
          </h4>

          <p>
            In order to register a different device, you must first disable
            two-factor authentication.
          </p>
          <el-form
            ref="formDisableRef"
            :model="form"
            :rules="rules"
            class="login-form"
            autocomplete="on"
            label-position="left"
          >
            <el-tooltip content="Caps lock is On" placement="right" manual>
              <el-form-item prop="password" :error="errors.password">
                <el-input
                  :key="passwordType"
                  ref="passwordRef"
                  v-model="form.password"
                  :type="passwordType"
                  placeholder="Password"
                  name="password"
                  tabindex="2"
                  @keyup.enter="onSubmitDisableTwoFa"
                >
                  <template #prefix>
                    <font-awesome-icon icon="lock" />
                  </template>
                  <template #suffix>
                    <span class="show-pwd" @click="showPwd">
                      <font-awesome-icon
                        :icon="
                          passwordType === 'password' ? 'eye' : 'eye-slash'
                        "
                      />
                    </span>
                  </template>
                </el-input>
              </el-form-item>
            </el-tooltip>

            <el-form-item>
              <el-button
                type="danger"
                plain
                :disabled="loading"
                :icon="loading ? 'LoadingElementIcon' : ''"
                @click.prevent="onSubmitDisableTwoFa"
              >
                Disable 2FA
              </el-button>
            </el-form-item>
          </el-form>
        </template>

        <template v-else>
          <h4>
            Status:
            <el-tag type="danger" size="small"> Disabled </el-tag>
          </h4>

          <p>
            We recommend cloud-based mobile authenticator apps such as Authy,
            Duo Mobile, and LastPass. They can restore access if you lose your
            hardware device.
          </p>

          <el-row :gutter="20">
            <el-col :span="6" :sm="9" :xs="24">
              <a :href="otp.url" target="_blank">
                <QRCodeVue3
                  :key="otp.url"
                  :value="otp.url"
                  :width="150"
                  :height="150"
                />
              </a>
              <br />
            </el-col>
            <el-col :span="18" :sm="15" :xs="24">
              <div class="two_fa_desc">
                <b>Can't scan the code?</b>
                <p>
                  To add the entry manually, provide the following details to
                  the application on your phone.
                </p>

                <p>
                  Secret:
                  <el-tooltip content="Copy to clipboard">
                    <span class="svg-container" @click="copyOtp">
                      {{ otp.secret }} <font-awesome-icon icon="copy" />
                    </span>
                  </el-tooltip>
                </p>
              </div>
            </el-col>
          </el-row>

          <el-form
            ref="formEnableRef"
            :model="form"
            :rules="rules"
            class="login-form"
            autocomplete="on"
            label-position="left"
          >
            <el-form-item prop="password" :error="errors.password">
              <el-input
                :key="passwordType"
                ref="passwordRef"
                v-model="form.password"
                :type="passwordType"
                placeholder="Password"
                name="password"
                tabindex="2"
              >
                <template #prefix>
                  <font-awesome-icon icon="lock" />
                </template>
                <template #suffix>
                  <span class="show-pwd" @click="showPwd">
                    <font-awesome-icon
                      :icon="passwordType === 'password' ? 'eye' : 'eye-slash'"
                    />
                  </span>
                </template>
              </el-input>
            </el-form-item>

            <el-form-item prop="pin" :error="errors.pin">
              <el-input
                v-model="form.pin"
                placeholder="Pin code"
                name="pin"
                @keyup.enter="onSubmitEnableTwoFa"
              >
                <template #prefix>
                  <span class="svg-container">
                    <font-awesome-icon icon="key" />
                  </span>
                </template>
              </el-input>
            </el-form-item>

            <el-form-item>
              <el-button
                type="success"
                plain
                :disabled="loading"
                :icon="loading ? 'LoadingElementIcon' : ''"
                @click.prevent="onSubmitEnableTwoFa"
              >
                Enable 2FA
              </el-button>
            </el-form-item>
          </el-form>
        </template>
      </el-col>
    </el-row>
  </el-card>
</template>

<script lang="ts">
import { ref, defineComponent, nextTick, reactive, computed } from 'vue'

import { ITwoFaAttributes, IUserOtpAttributes } from '@/api/users'
import { ProfileValidationErrorsKey } from '@/api/types'

import useValidation from '@/hooks/useValidation'

import { useUserStore } from '@/store/modules/user'
import QRCodeVue3 from 'qrcode-vue3'
import copy from 'copy-to-clipboard'

import { ElMessage, FormInstance } from 'element-plus'
import useRouter from '@/hooks/useRouter'
import { safeSubmitProcessing } from '@/utils/helpers'
import twoFaIcon from '@/apps/traforama/assets/icons/2fa.png'

export default defineComponent({
  name: 'TwoFactorForm',

  components: {
    QRCodeVue3
  },
  setup() {
    const userStore = useUserStore()
    const { router } = useRouter()
    const { provideValidation, setValidationErrors } = useValidation(
      ProfileValidationErrorsKey
    )
    const errors = provideValidation()

    const loading = ref(true)
    const enabled = ref(false)
    const passwordRef = ref()
    const formDisableRef = ref<FormInstance | null>(null)
    const formEnableRef = ref<FormInstance | null>(null)
    const passwordType = ref('password')
    const otp = ref({ required: true } as IUserOtpAttributes)

    const rules = computed(() => ({
      password: {
        required: true,
        message: 'This field is required',
        trigger: 'change'
      },
      ...(!enabled.value && {
        pin: {
          required: true,
          length: 6,
          message: 'Should be 6 digits',
          trigger: 'change'
        }
      })
    }))

    let form = reactive({
      password: '',
      pin: ''
    } as ITwoFaAttributes)

    const init = () => {
      otp.value = userStore.otpInfo
      enabled.value = userStore.otpInfo.required
      loading.value = false
    }
    userStore.FetchUserOtpInfo().then(() => {
      init()
    })

    const copyOtp = () => {
      copy(otp.value.secret || '')
      ElMessage.success({ message: 'Copied to clipboard!', type: 'success' })
    }

    const showPwd = () => {
      if (passwordType.value === 'password') {
        passwordType.value = ''
      } else {
        passwordType.value = 'password'
      }
      nextTick(() => {
        passwordRef.value.focus()
      })
    }

    const onDisable = () => {
      form.password = ''
      form.pin = ''
      init()
      ElMessage.success({ message: '2FA Disabled' })
    }

    const onSubmitDisableTwoFa = () => {
      if (!formDisableRef.value) return

      formDisableRef.value.validate(async (valid: boolean) => {
        if (valid) {
          await safeSubmitProcessing(
            loading,
            () => userStore.DisableTwoFa(form),
            () => onDisable(),
            (err) => setValidationErrors(err, errors)
          )
        }
      })
    }

    const onEnable = () => {
      router.push('/login')
      ElMessage.success({ message: '2FA Enabled. Please login' })
    }

    const onSubmitEnableTwoFa = () => {
      if (!formEnableRef.value) return

      formEnableRef.value.validate(async (valid: boolean) => {
        if (valid) {
          await safeSubmitProcessing(
            loading,
            () => userStore.EnableTwoFa(form),
            () => onEnable(),
            (err) => setValidationErrors(err, errors)
          )
        }
      })
    }

    const value = reactive({})

    return {
      rules,
      errors,
      otp,
      form,
      loading,
      enabled,
      passwordRef,
      formEnableRef,
      formDisableRef,
      passwordType,
      init,
      showPwd,
      copyOtp,
      onSubmitDisableTwoFa,
      onSubmitEnableTwoFa,
      twoFaIcon
    }
  }
})
</script>
<style lang="scss" scoped>
.two_fa_desc {
  min-height: 150px;
  padding: 10px;
  background-color: #fafafa;
  border: 1px solid #dbdbdb;
  border-radius: 0.25rem;
  margin-bottom: 20px;
}
</style>
